import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { wallIsCartesian } from '@geometry'
import { getLength } from '@scene'
import { StructuralPlanningElementFormHeader } from '@structuralPlanningComponents'
import { v4 } from 'uuid'
import { Button, Stack } from '@mui/material'
import CreateElementLabelField from '@editor/pages/StructuralPlanning/components/CreateElementLabelField'
import {
  applyCreateLabel,
  createLabelSchema,
} from '@editor/pages/StructuralPlanning/components/RectangularShape/components/RectangularShapeForm/components/GeometryForm/schema'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import {
  DeleteButton,
  HotkeyDeleteButton,
  HotkeySaveButton,
  SaveButton,
  SwitchLabeled,
} from '@ui/actions'
import { Form } from '@ui/forms'
import { useControlStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import { CollapsibleGeometryBoxSection } from '@editorComponents'
import FormFields from '../FormFields'
import { wallSchema } from './schema'

interface Props {
  handleSubmit: (wall: ShapeObject) => void
  isSubmitting?: boolean
  handleDelete: () => void
  handleReset?: () => void
  isResettable?: boolean
  isDeleting?: boolean
  wall: ShapeObject
  isPlacementEditable?: boolean
  isSaveable?: boolean
  onClose?: () => void
  showCreateLabel?: boolean
}

const GeometryForm = ({
  handleDelete,
  handleSubmit,
  handleReset,
  isSubmitting,
  isDeleting,
  wall,
  isSaveable = false,
  isResettable = false,
  isPlacementEditable = true,
  onClose,
  showCreateLabel = false,
}: Props): ReactElement => {
  const getLabel = useElementLabel()

  const label = getLabel(wall.guid)
  const isCartesian = wallIsCartesian(wall)
  const intelligentWallEditEnabled = useControlStore(state => state.intelligentWallEditEnabled)
  const setIntelligentWallEditEnabled = useControlStore(
    state => state.setIntelligentWallEditEnabled,
  )

  const defaultValues = useMemo(() => {
    const [start, end] = wall.shape.points

    return {
      guid: wall.guid,
      start: {
        x: start.x,
        y: start.y,
        z: start.z,
      },
      end: {
        x: end.x,
        y: end.y,
        z: end.z,
      },
      lengthAdjustment: 0,
      preservationMode: 'start' as const,
      placement: wall.placement ?? 'Internal',
      ...(showCreateLabel ? { label: '' } : {}),
    }
  }, [wall, showCreateLabel])

  const [formStateRandomId, setFormStateRandomId] = useState(v4())

  const SubmitButton = showCreateLabel ? HotkeySaveButton : SaveButton
  const RemoveButton = showCreateLabel ? HotkeyDeleteButton : DeleteButton

  return (
    <>
      <Form
        key={`${wall.guid}-${formStateRandomId}`}
        onSubmit={state => {
          handleSubmit(showCreateLabel ? applyCreateLabel(wall, state.label) : wall)
          setFormStateRandomId(v4())
        }}
        validationSchema={showCreateLabel ? wallSchema.concat(createLabelSchema) : wallSchema}
        defaultValues={defaultValues}
      >
        <FormValueUpdater wall={wall} />
        <Stack direction="column" spacing={1}>
          {showCreateLabel ? (
            <CreateElementLabelField />
          ) : (
            <StructuralPlanningElementFormHeader
              elementGuid={wall.guid}
              label={label}
              variant="h6"
              onClose={onClose}
            />
          )}

          <CollapsibleGeometryBoxSection label="Geometrie" defaultExpanded={true}>
            <Stack direction="column" spacing={1}>
              <FormFields
                wall={wall}
                isPlacementEditable={isPlacementEditable}
                handleReset={handleReset}
              />

              {isCartesian && (
                <SwitchLabeled
                  checked={intelligentWallEditEnabled}
                  onChange={event => setIntelligentWallEditEnabled(event.target.checked)}
                  label="Intelligentes Verschieben"
                  data-cy="switch-intelligent-wall-edit"
                  infoTooltip={{
                    title:
                      'Beim orthogonalen Verschieben werden angeschlossene, kartesische Wände (Start/Ende genau auf dieser Wand) mit verkürzt oder verlängert.',
                  }}
                />
              )}

              <Stack direction="row" justifyContent="end" spacing={1}>
                <SubmitButton
                  type="submit"
                  loading={isSubmitting}
                  fullWidth
                  disabled={isDeleting || !isSaveable}
                  data-cy="btn-wall-geometry-form-save"
                >
                  Speichern
                </SubmitButton>
                {isResettable && (
                  <Button variant="outlined" onClick={handleReset} fullWidth>
                    Zurücksetzen
                  </Button>
                )}
                <RemoveButton
                  onClick={handleDelete}
                  size="small"
                  fullWidth
                  loading={isDeleting}
                  disabled={isSubmitting}
                  data-cy="delete-wall-btn"
                >
                  Löschen
                </RemoveButton>
              </Stack>
            </Stack>
          </CollapsibleGeometryBoxSection>
        </Stack>
      </Form>
    </>
  )
}

// Component to update form values when wall geometry changes
const FormValueUpdater = ({ wall }: { wall: ShapeObject }): null => {
  const {
    setValue,
    formState: { defaultValues },
  } = useFormContext()

  useEffect(() => {
    const [start, end] = wall.shape.points

    // Update form field values without changing default values
    setValue(
      'start',
      {
        x: start.x,
        y: start.y,
        z: start.z,
      },
      { shouldDirty: false },
    )

    setValue(
      'end',
      {
        x: end.x,
        y: end.y,
        z: end.z,
      },
      { shouldDirty: false },
    )

    // is_updated is encoded in the other way around in the form
    // is_updated === 'Start' means that the end point is fixed
    const preservationMode =
      wall.is_updated === 'End' ? 'start' : wall.is_updated === 'Start' ? 'end' : 'start'
    const newLength = getLength(
      new ImmutableVector3(start.x, start.y, start.z),
      new ImmutableVector3(end.x, end.y, end.z),
    )
    const originalLength = defaultValues
      ? getLength(
          new ImmutableVector3(
            defaultValues?.start.x,
            defaultValues?.start.y,
            defaultValues?.start.z,
          ),
          new ImmutableVector3(defaultValues?.end.x, defaultValues?.end.y, defaultValues?.end.z),
        )
      : newLength
    const lengthAdjustment = newLength - originalLength
    // Reset length adjustment fields when wall geometry changes
    setValue('lengthAdjustment', lengthAdjustment, { shouldDirty: false })
    setValue('preservationMode', preservationMode, { shouldDirty: false })
  }, [wall.shape.points, setValue, defaultValues, wall.is_updated])

  return null
}

export default GeometryForm
